/**
 * ScenarioLab Configuration Components
 * 
 * Quick presets selection and scenario configuration panel.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { 
  Beaker,
  Play,
  Settings,
  RotateCcw,
  Copy,
  ChevronDown,
  ChevronUp,
  Loader2
} from 'lucide-react';
import { Toggle, Select, MultiSelect } from './shared';

// Quick Presets Card
export const QuickPresets = ({ presets, onApply }) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="pb-3">
      <CardTitle className="text-white text-base flex items-center gap-2">
        <Beaker className="w-4 h-4 text-cyan-400" />
        Quick Presets
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      <div className="flex flex-wrap gap-2">
        {presets?.map(preset => (
          <Button
            key={preset.id}
            variant="outline"
            size="sm"
            onClick={() => onApply(preset)}
            className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            {preset.name}
          </Button>
        ))}
      </div>
    </CardContent>
  </Card>
);

// Scenario Configuration Panel
export const ConfigurationPanel = ({ 
  config, 
  setConfig, 
  parameters, 
  showAdvanced, 
  setShowAdvanced,
  onRun,
  onReset,
  onCopy,
  loading 
}) => (
  <Card className="bg-slate-800/50 border-slate-700">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <Settings className="w-4 h-4 text-cyan-400" />
          Scenario Configuration
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCopy} className="text-slate-400 hover:text-white">
            <Copy className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onReset} className="text-slate-400 hover:text-white">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </CardHeader>
    <CardContent className="pt-0 space-y-4">
      {/* Passenger Configuration */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-slate-400 text-xs mb-1 block">Passenger Name</Label>
          <Input
            value={config.passenger_name}
            onChange={(e) => setConfig(prev => ({ ...prev, passenger_name: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white h-9"
          />
        </div>
        <Select
          label="Mobility"
          value={config.mobility_requirement}
          onChange={(v) => setConfig(prev => ({ ...prev, mobility_requirement: v }))}
          options={parameters.mobility_requirements}
        />
        <Select
          label="Appointment Urgency"
          value={config.appointment_urgency}
          onChange={(v) => setConfig(prev => ({ ...prev, appointment_urgency: v }))}
          options={parameters.appointment_urgency}
        />
        <div>
          <Label className="text-slate-400 text-xs mb-1 block">Appointment Time</Label>
          <Input
            type="time"
            value={config.appointment_time}
            onChange={(e) => setConfig(prev => ({ ...prev, appointment_time: e.target.value }))}
            className="bg-slate-800 border-slate-600 text-white h-9"
          />
        </div>
      </div>
      
      {/* Accessibility Toggles */}
      <div className="flex gap-6">
        <Toggle
          checked={config.requires_lift_access}
          onChange={() => setConfig(prev => ({ ...prev, requires_lift_access: !prev.requires_lift_access }))}
          label="Requires Lift Access"
        />
        <Toggle
          checked={config.requires_staff_assistance}
          onChange={() => setConfig(prev => ({ ...prev, requires_staff_assistance: !prev.requires_staff_assistance }))}
          label="Requires Staff Assistance"
        />
      </div>
      
      {/* Disruption Configuration */}
      <div className="border-t border-slate-700 pt-4">
        <MultiSelect
          label="Disruption Types"
          values={config.disruption_types}
          onChange={(v) => setConfig(prev => ({ ...prev, disruption_types: v }))}
          options={parameters.disruption_types}
        />
        <div className="mt-3">
          <Select
            label="Disruption Severity"
            value={config.disruption_severity}
            onChange={(v) => setConfig(prev => ({ ...prev, disruption_severity: v }))}
            options={parameters.disruption_severity}
          />
        </div>
      </div>
      
      {/* Advisory Sources - Collapsible */}
      <div className="border-t border-slate-700 pt-4">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"
        >
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Advisory Source Configuration
        </button>
        
        {showAdvanced && (
          <div className="mt-3 space-y-3 pl-4 border-l-2 border-slate-700">
            <div className="flex flex-wrap gap-4">
              <Toggle
                checked={config.include_verified_operator}
                onChange={() => setConfig(prev => ({ ...prev, include_verified_operator: !prev.include_verified_operator }))}
                label="Verified Operator"
              />
              <Toggle
                checked={config.include_verified_network}
                onChange={() => setConfig(prev => ({ ...prev, include_verified_network: !prev.include_verified_network }))}
                label="Verified Network"
              />
              <Toggle
                checked={config.include_station_staff}
                onChange={() => setConfig(prev => ({ ...prev, include_station_staff: !prev.include_station_staff }))}
                label="Station Staff"
              />
            </div>
            
            <div className="flex items-end gap-4">
              <Toggle
                checked={config.include_unverified_source}
                onChange={() => setConfig(prev => ({ ...prev, include_unverified_source: !prev.include_unverified_source }))}
                label="Include Unverified Source"
              />
              {config.include_unverified_source && (
                <div className="flex-1 max-w-xs">
                  <Select
                    label="Unverified Advisory Type"
                    value={config.unverified_advisory_type}
                    onChange={(v) => setConfig(prev => ({ ...prev, unverified_advisory_type: v }))}
                    options={parameters.unverified_advisory_types}
                  />
                </div>
              )}
            </div>
            
            <div className="max-w-xs">
              <Label className="text-slate-400 text-xs mb-1 block">Test Run ID (optional)</Label>
              <Input
                value={config.test_run_id}
                onChange={(e) => setConfig(prev => ({ ...prev, test_run_id: e.target.value }))}
                placeholder="e.g., PARTNER-TEST-001"
                className="bg-slate-800 border-slate-600 text-white h-9"
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Run Button */}
      <Button
        onClick={onRun}
        disabled={loading}
        className="w-full bg-cyan-600 hover:bg-cyan-700"
        data-testid="run-scenario-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Running Scenario...
          </>
        ) : (
          <>
            <Play className="w-4 h-4 mr-2" />
            Run Scenario
          </>
        )}
      </Button>
    </CardContent>
  </Card>
);
